import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, MapPin, LayoutGrid, Pencil, Power, Send, Loader2, X } from "lucide-react";
import apiClient from "@/lib/api";
import OwnerLayout from "@/components/owner/OwnerLayout";
import PropertyStatusBadge, { PROPERTY_TYPE_LABELS, GENDER_LABELS } from "@/components/property/PropertyStatusBadge";
import ListingPanel from "@/components/property/ListingPanel";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "-"; }
}

export default function OwnerPropertiesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [acting, setActing] = useState(false);
  const [reviewProp, setReviewProp] = useState(null);

  const load = () => {
    setLoading(true);
    apiClient.get("/properties").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDeactivate = async () => {
    setActing(true);
    try {
      await apiClient.delete(`/properties/${confirmId}`);
      toast.success("Property dinonaktifkan");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error("Gagal menonaktifkan property");
    } finally {
      setActing(false);
    }
  };

  return (
    <OwnerLayout testid="owner-properties-page">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Property Saya</h1>
          <p className="mt-2 text-sm text-muted-foreground">Daftar property yang Anda kelola.</p>
        </div>
        <Link to="/owner/properties/new" data-testid="add-property-button"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-105 active:scale-95 transition-transform">
          <PlusCircle size={18} /> Tambah Property
        </Link>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20" data-testid="properties-loading">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/30 p-14 text-center" data-testid="properties-empty">
          <h3 className="font-heading text-xl font-semibold">Belum ada property</h3>
          <p className="mt-2 text-sm text-muted-foreground">Mulai dengan menambahkan property pertama Anda.</p>
          <Link to="/owner/properties/new" className="mt-4 inline-block px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">
            Tambah Property
          </Link>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="properties-list">
          {items.map((p, i) => (
            <div key={p.id} data-testid={`property-item-${i}`}
              className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col sm:flex-row hover:shadow-md transition-shadow">
              {/* photo placeholder */}
              <div className="sm:w-48 h-40 sm:h-auto bg-secondary/60 grid place-items-center shrink-0 text-xs text-muted-foreground" data-testid={`property-photo-${i}`}>
                Foto belum tersedia
              </div>
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg font-semibold truncate" data-testid={`property-name-${i}`}>{p.name}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPin size={14} /> {p.location?.city_name}, {p.location?.district_name}
                    </p>
                  </div>
                  <PropertyStatusBadge status={p.status} testid={`property-status-${i}`} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-secondary/60 text-muted-foreground">{PROPERTY_TYPE_LABELS[p.property_type]}</span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary/60 text-muted-foreground">{GENDER_LABELS[p.gender_type]}</span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary/60 text-muted-foreground">Dibuat {formatDate(p.created_at)}</span>
                  {p.min_price != null && (
                    <span className="px-2.5 py-1 rounded-full bg-secondary/60 text-primary font-semibold">Rp {p.min_price.toLocaleString("id-ID")}/bln</span>
                  )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => navigate(`/owner/properties/${p.id}/edit`)} data-testid={`property-edit-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                    <Pencil size={15} /> Edit
                  </button>
                  <button onClick={() => navigate(`/owner/properties/${p.id}`)} data-testid={`property-manage-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                    <LayoutGrid size={15} /> Kelola Kamar & Foto
                  </button>
                  <button onClick={() => setReviewProp(p)} data-testid={`property-review-${i}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
                    <Send size={15} /> Ajukan Review
                  </button>
                  {p.status !== "inactive" && (
                    <button onClick={() => setConfirmId(p.id)} data-testid={`property-deactivate-${i}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-destructive/40 text-sm font-semibold text-destructive hover:bg-destructive/10 transition-colors">
                      <Power size={15} /> Nonaktifkan
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm dialog */}
      {confirmId && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="deactivate-confirm">
          <div className="bg-card rounded-2xl border border-border p-6 max-w-sm w-full">
            <h3 className="font-heading text-lg font-semibold">Nonaktifkan property?</h3>
            <p className="mt-2 text-sm text-muted-foreground">Property akan disembunyikan (status inactive). Anda tetap dapat mengedit kembali nanti.</p>
            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => setConfirmId(null)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold" data-testid="deactivate-cancel">
                Batal
              </button>
              <button onClick={handleDeactivate} disabled={acting} data-testid="deactivate-confirm-button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive text-destructive-foreground text-sm font-semibold disabled:opacity-60">
                {acting && <Loader2 size={14} className="animate-spin" />} Nonaktifkan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ajukan Review modal — Status Listing + submit + link Kelola Kamar & Foto */}
      {reviewProp && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="review-modal"
          onClick={() => setReviewProp(null)}>
          <div className="bg-card rounded-2xl border border-border w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-5 border-b border-border sticky top-0 bg-card z-10">
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-semibold">Ajukan Review</h3>
                <p className="text-sm text-muted-foreground truncate">{reviewProp.name}</p>
              </div>
              <button onClick={() => setReviewProp(null)} data-testid="review-modal-close"
                className="grid place-items-center w-9 h-9 rounded-full border border-border text-muted-foreground hover:bg-secondary/60 transition-colors shrink-0">
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              <ListingPanel property={reviewProp} onChanged={() => { load(); setReviewProp(null); }} />
              <div className="mt-6 pt-5 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Perlu melengkapi kamar atau foto dulu?</p>
                <Link to={`/owner/properties/${reviewProp.id}`} data-testid="review-manage-link"
                  onClick={() => setReviewProp(null)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:scale-[1.02] active:scale-95 transition-transform">
                  <LayoutGrid size={15} /> Kelola Kamar & Foto
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </OwnerLayout>
  );
}
